/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { ServerInfo } from 'src/orchd-client';
import dayjs from 'dayjs';
import clamp from 'lodash/clamp';
import { AxisOptions, Chart } from 'react-charts';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { useGetServerStatsQuery } from 'src/api_services/servers/query';
import { Box } from 'src/components/Box';
import { FailedToLoad } from 'src/components/FailedToLoad';
import { Icon } from 'src/components/Icon';
import { Loading } from 'src/components/Loading';
import { ServerStatusCard } from 'src/components/ServerStatusCard';
import { Text } from 'src/components/Text';
import { viewportSelectors } from 'src/containers/Viewport/_redux/selectors';
import { AppState } from 'src/store';
import { globalUiSelectors } from 'src/store/globalUi/selectors';
import { dateTimeConfig, timeConfig } from 'src/translations/constants';
import { getDurationAsMilliseconds } from 'src/utils/dateTimes/dateTimes';
import { intervalDataToLineGraph } from 'src/utils/graphs/graphs';

import { style } from '../../ServerUsage/ServerUsage.style';

type StateProps = ReturnType<typeof stateToProps>;

export interface Props extends StateProps {
  server: ServerInfo;
}

const stateToProps = (state: AppState) => ({
  isTabletUp: viewportSelectors.isTabletUp(state),
  darkMode: globalUiSelectors.darkMode(state),
});

type Datum = { interval: Date; resourceCount: number };

export const _LoadCard = ({ server, isTabletUp, darkMode }: Props) => {
  const { formatDate } = useIntl();
  const [collapsed, setIsCollapsed] = useState(true);

  const { formatMessage } = useIntl();

  const {
    data: serverStats = [],
    isLoading,
    isError,
  } = useGetServerStatsQuery({ serverId: server.id, refetchInterval: 30_000 });

  const lineData = useMemo(() => {
    const nearestFifthMinute =
      Math.floor(Date.now() / getDurationAsMilliseconds(5, 'minutes')) * getDurationAsMilliseconds(5, 'minutes');

    const data = intervalDataToLineGraph({
      data: serverStats.map((stat) => ({
        interval: stat.recordedAt,
        resourceCount: stat.systemLoad,
      })),
      numberBack: clamp(serverStats.length, 2, 300),
      start: dayjs(new Date(nearestFifthMinute)),
      interval: 'minute',
      intervalCount: 5,
    });

    return {
      label: formatMessage({ id: 'manager_server.server_load.load', defaultMessage: 'load' }),
      data,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverStats]);

  const maxValue = useMemo(() => {
    return Math.max(0, ...serverStats.map((stat) => stat.systemLoad));
  }, [serverStats]);
  const max = maxValue === 0 ? maxValue + 1 : maxValue * 1.2;

  const primaryAxis = useMemo(
    (): AxisOptions<Datum> => ({
      getValue: (datum) => datum.interval,
      scaleType: 'time',
      max: lineData.data[lineData.data.length - 1]?.interval,
      min: lineData.data[0]?.interval,
      tickCount: isTabletUp ? 25 : 12,
      formatters: {
        tooltip: (value) => formatDate(dayjs(value).format(), dateTimeConfig),
        scale: (value) => formatDate(dayjs(value).format(), timeConfig),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineData.data, isTabletUp]
  );

  const secondaryAxes = useMemo(
    (): AxisOptions<Datum>[] => [
      {
        getValue: (datum) => datum.resourceCount,
        scaleType: 'linear',
        max,
        showDatumElements: true,

        formatters: {
          tooltip: (value) => value?.toFixed(2),
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineData.data]
  );

  return (
    <ServerStatusCard padding={{ xs: 'lg', sm: 'xl' }} marginBottom="0" fullHeight>
      <Box css={style.usageCardHeader} j="space-between" onClick={() => setIsCollapsed(!collapsed)} a="center">
        <span>
          {/* todo - StatusIcon type needs to be dynamic https://app.asana.com/0/1154416020287572/1169149334678323 */}
          {/* <StatusIcon css={style.usageStatusIcon} type={error ? 'error' : 'success'} /> */}

          <Text size={{ xs: 'md', sm: 'lg' }}>
            <FormattedMessage id="manage_server.server_load" defaultMessage="Load monitor" />
          </Text>
        </span>

        {!isTabletUp && <Icon name={collapsed ? 'chevron down' : 'chevron up'} />}
      </Box>

      {(isTabletUp || !collapsed) && (
        <Box css={style.tallCardBody} position="relative" mt="xl">
          {isError && <FailedToLoad smallText fullHeight />}

          {isLoading && !isError && serverStats.length === 0 && <Loading absoluteCenter />}

          {!isError && (
            <Chart
              options={{
                data: [lineData],
                primaryAxis,
                secondaryAxes,
                defaultColors: ['#1BB341'],
                primaryCursor: {
                  showLabel: false,
                },
                secondaryCursor: {
                  showLabel: false,
                },
                dark: darkMode,
              }}
            />
          )}
        </Box>
      )}
    </ServerStatusCard>
  );
};

export const LoadCard = connect(stateToProps)(memo(_LoadCard));
